import React, { useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Calendar from './Main';
import * as BookingActions from '../../../../actions/booking';
import * as TimeslotActions from '../../../../actions/timeslot';
import { withTranslation } from 'next-i18next';
import { useHolidaysQuery } from '../../../../hooks/queries';

interface Tutor {
  id: string | number;
}

interface SlotStatus {
  timestamp: number;
  status: string;
}

interface Booking {
  id: string | number;
  start_at: number;
  end_at: number;
}

interface Holiday {
  date: string;
}

interface GeoData {
  timeZone: string;
}

interface CurrentUser {
  js_time_zone?: string;
}

interface BookingActionsType {
  fetchMyBookings: () => void;
}


interface TimeslotActionsType {
  fetchByTutor: (
    tutorId: string | number,
    options: { timeZone: string },
  ) => void;
}

interface RootState {
  tutorTimeslot: {
    slotStatuses: SlotStatus[];
    loading: boolean;
  };
  booking: {
    myBookings: Booking[];
    loading: boolean;
  };
  geoData: {
    geoData: GeoData;
  };
  auth: {
    user: CurrentUser | null;
  };
}

interface BookingCalendarContainerProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  tutor: Tutor;
  duration: number;
  slotStatuses?: SlotStatus[];
  loadingSlotStatuses?: boolean;
  myBookings?: Booking[];
  loadingBookings?: boolean;
  geoData?: GeoData;
  currentUser?: CurrentUser | null;
  bookingActions?: BookingActionsType;
  timeslotActions?: TimeslotActionsType;
}

function mapStateToProps(state: RootState) {
  const { tutorTimeslot, booking, geoData, auth } = state;
  const { myBookings } = booking;
  return {
    slotStatuses: tutorTimeslot.slotStatuses,
    loadingSlotStatuses: tutorTimeslot.loading,
    myBookings,
    loadingBookings: booking.loading,
    geoData: geoData.geoData,
    currentUser: auth.user,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    bookingActions: bindActionCreators(
      BookingActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ) as unknown as BookingActionsType,
    timeslotActions: bindActionCreators(
      TimeslotActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ) as unknown as TimeslotActionsType,
  };
}

const BookingCalendarContainer: React.FC<BookingCalendarContainerProps> = (
  props,
) => {
  const {
    tutor,
    geoData,
    bookingActions,
    timeslotActions,
    slotStatuses,
    duration,
    myBookings,
    loadingSlotStatuses,
    loadingBookings,
    currentUser,
  } = props;

  // Use TanStack Query for holidays data
  const { data: holidays = [], isSuccess: holidaysLoaded } = useHolidaysQuery();

  const timeZone = currentUser?.js_time_zone || geoData!.timeZone;
  useEffect(() => {
    bookingActions!.fetchMyBookings();
  }, [bookingActions]);
  useEffect(() => {
    if (tutor && timeZone) {
      timeslotActions!.fetchByTutor(tutor.id, { timeZone });
    }
  }, [tutor, timeZone, timeslotActions]);

  const fetchSlotStatuses = (
    tutorId: string | number,
    passedTimeZone: string,
  ) => {
    timeslotActions!.fetchByTutor(tutorId, { timeZone: passedTimeZone });
  };

  let calendar = <div />;
  if (tutor && holidaysLoaded && !loadingSlotStatuses && !loadingBookings) {
    calendar = (
      <Calendar
        tutor={tutor}
        slotStatuses={slotStatuses}
        duration={duration}
        holidays={holidays}
        myBookings={myBookings}
        loadingSlotStatuses={loadingSlotStatuses}
        timeZone={timeZone}
      />
    );
  }
  const panelBodyClass = classnames('mt-4', {
    loading: loadingSlotStatuses || loadingBookings,
  });
  return (
    <div className="calendar-content">
      <div className={panelBodyClass}>
        <div className="cal week-scroll">
          <div className="cal-container">{calendar}</div>
        </div>
      </div>
    </div>
  );
};

const wrapped = withTranslation('common')(BookingCalendarContainer);

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
